import { FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@getgo/chameleon-web-react-wrapper';

import { useAppDispatch, useAppSelector } from 'hooks';
import { postProcessRenewal } from 'modules/cog';
import { isProcessRenewalLoading } from 'modules/cog/cog-selector';
import { showErrorSnack } from 'modules/error';
import { sessionId } from 'modules/global-wrapper';
import { isDeletePaymentMethodLoading, isSetDefaultPaymentLoading, paymentMethodTypes } from 'modules/payment-methods';
import {
  isEcomClient,
  isNewTabPayment,
  sessionClientURL,
  sessionCurrencyCode,
  sessionInvoiceNumber,
  sessionProducts,
  sessionQuoteId,
  sessionTotalAmount,
  sesssionCancelRedirectUrl,
} from 'modules/session-details';
import Track, { GAEventNames, GATrack, PayNowEventNames } from 'modules/tracking';
import { getPaymentTrackingType, getPurchaseFlow, trackRevenue } from 'modules/tracking/tracking-module';
import {
  postTransactionUseDDPay,
  transactionsDDIsLoading,
  useDDPayFailed,
  useDDPayPending,
  useDDPaySuccess,
} from 'modules/transaction-dd';
import {
  postTransactionUsePaypalPay,
  transactionsPaypalIsLoading,
  usePaypalPayFailed,
  usePaypalPayPending,
  usePaypalPaySuccess,
} from 'modules/transaction-paypal';
import {
  postTransactionUseRazorPay,
  razorPayIsFailed,
  razorPayIsPending,
  razorPayIsSuccess,
  useRazorpayIsLoading,
} from 'modules/transaction-razorpay';
import {
  postTransactionUseAchPay,
  postTransactionUseAdyenAchPay,
  transactionsAchIsLoading,
  useAchPayFailed,
  useAchPayPending,
  useAchPaySuccess,
} from 'modules/transactions-ach';
import { transactionFingerprintingId } from 'modules/transactions-cc';
import { postProcessInvoice, postProcessQuote } from 'modules/ucs';
import { isProcessInvoiceLoading, isProcessQuoteLoading } from 'modules/ucs/ucs-selector';
import { PAYMENT_METHODS_TYPES, SEARCH_PARAMS, TRACKING_EVENT_STATUS } from 'utils/constants';
import st from 'utils/shared-translations';
import { getProductAdminDataFromCookie, removeProductAdminDataFromCookie } from 'utils/ui-utils';

interface ElectronicBuyNowProps {
  paymentMethodKey: string;
  paymentType: (typeof PAYMENT_METHODS_TYPES)[keyof typeof PAYMENT_METHODS_TYPES];
  isAdyen?: boolean;
}

let isDisabled = false;

const ElectronicBuyNow: FC<ElectronicBuyNowProps> = ({ paymentMethodKey, paymentType, isAdyen }): JSX.Element => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const [isComponentLoading, setComponentLoading] = useState(false);

  const selectedSessionId = useAppSelector(sessionId);
  const selectedIsNewTabPayment = useAppSelector(isNewTabPayment);
  const selectedQuoteProcessIsLoading = useAppSelector(isProcessQuoteLoading);
  const selectedProcessRenewalIsLoading = useAppSelector(isProcessRenewalLoading);
  const selectedProcessInvoiceIsLoading = useAppSelector(isProcessInvoiceLoading);
  const renewalDate = localStorage.getItem(SEARCH_PARAMS.renewalDate) || '';

  const selectedFingerprintId = useAppSelector(transactionFingerprintingId);

  // Session details selectors
  const selectedQuoteTotalPrice = useAppSelector(sessionTotalAmount);
  const selectedQuoteKey = useAppSelector(sessionQuoteId);
  const selectedSessionProducts = useAppSelector(sessionProducts);
  const selectedSessionCurrencyCode = useAppSelector(sessionCurrencyCode);
  const selectedSessionCancelUrl = useAppSelector(sesssionCancelRedirectUrl);
  const selectedClientIsEcom = useAppSelector(isEcomClient);
  const selectedSessionClientUrl = useAppSelector(sessionClientURL);
  const selectedInvoiceNumber = useAppSelector(sessionInvoiceNumber);

  // ACH selectors
  const selectedTransactionAchLoading = useAppSelector(transactionsAchIsLoading);
  const selectedTransactionUseAchFailed = useAppSelector(useAchPayFailed);
  const selectedTransactionUseAchSuccess = useAppSelector(useAchPaySuccess);
  const selectedTransactionUseAchPending = useAppSelector(useAchPayPending);

  // DD selectors
  const selectedTransactionDDLoading = useAppSelector(transactionsDDIsLoading);
  const selectedTransactionUseDDFailed = useAppSelector(useDDPayFailed);
  const selectedTransactionUseDDSuccess = useAppSelector(useDDPaySuccess);
  const selectedTransactionUseDDPending = useAppSelector(useDDPayPending);

  // Paypal selectors
  const selectedTransactionPaypalLoading = useAppSelector(transactionsPaypalIsLoading);
  const selectedTransactionUsePaypalFailed = useAppSelector(usePaypalPayFailed);
  const selectedTransactionUsePaypalSuccess = useAppSelector(usePaypalPaySuccess);
  const selectedTransactionUsePaypalPending = useAppSelector(usePaypalPayPending);

  // Razorpay selectors
  const selectedUseRazorPayLoading = useAppSelector(useRazorpayIsLoading);
  const selectedRazorpayIsFailed = useAppSelector(razorPayIsFailed);
  const selectedRazorpayIsSuccess = useAppSelector(razorPayIsSuccess);
  const selectedRazorpayIsPending = useAppSelector(razorPayIsPending);

  const selectedDeletePaymentMethodIsLoading = useAppSelector(isDeletePaymentMethodLoading);
  const selectedSetDefaultPaymentIsLoading = useAppSelector(isSetDefaultPaymentLoading);
  const selectedPaymentMethodTypes = useAppSelector(paymentMethodTypes);

  const productAdminData = getProductAdminDataFromCookie();

  const processQuote = useCallback(() => {
    const data = {
      quoteCode: selectedQuoteKey,
      payload: {
        grossAmount: selectedQuoteTotalPrice,
        sessionId: selectedSessionId,
        paymentType,
        ...(Object.keys(productAdminData).length > 0 && { productAdmin: productAdminData }),
      },
    };

    dispatch(postProcessQuote(data))
      .unwrap()
      .then(() =>
        trackRevenue({
          orderList: selectedSessionProducts,
          currencyCode: selectedSessionCurrencyCode,
          purchaseFlow: getPurchaseFlow(selectedClientIsEcom, selectedSessionCancelUrl),
          paymentMethod: getPaymentTrackingType(paymentType, selectedPaymentMethodTypes),
          quoteId: selectedQuoteKey,
          clientURL: selectedSessionClientUrl,
          total: selectedQuoteTotalPrice,
        }),
      )
      .catch(() => {
        GATrack(GAEventNames.PurchaseError, {
          products: selectedSessionProducts,
          paymentMethod: paymentType,
          errMsg: GAEventNames.PROVISIONING_FAILED,
        });
      })
      .finally(() => Object.keys(productAdminData).length > 0 && removeProductAdminDataFromCookie());
    // eslint-disable-next-line
  }, [
    dispatch,
    selectedQuoteKey,
    selectedQuoteTotalPrice,
    selectedSessionCancelUrl,
    selectedSessionCurrencyCode,
    selectedSessionId,
    selectedSessionProducts,
    paymentType,
  ]);

  const processRenwal = useCallback(() => {
    const payload = {
      date: renewalDate,
      sessionId: selectedSessionId,
      amount: selectedQuoteTotalPrice.toString(),
    };
    dispatch(postProcessRenewal(payload))
      .unwrap()
      .then(() =>
        trackRevenue({
          orderList: selectedSessionProducts,
          currencyCode: selectedSessionCurrencyCode,
          purchaseFlow: getPurchaseFlow(selectedClientIsEcom, selectedSessionCancelUrl),
          paymentMethod: getPaymentTrackingType(paymentType, selectedPaymentMethodTypes),
          quoteId: selectedQuoteKey,
          clientURL: selectedSessionClientUrl,
          total: selectedQuoteTotalPrice,
        }),
      )
      .catch(() => {});
  }, [
    dispatch,
    paymentType,
    renewalDate,
    selectedClientIsEcom,
    selectedQuoteKey,
    selectedQuoteTotalPrice,
    selectedSessionCancelUrl,
    selectedSessionClientUrl,
    selectedSessionCurrencyCode,
    selectedSessionId,
    selectedSessionProducts,
    selectedPaymentMethodTypes,
  ]);

  const processInvoice = useCallback(() => {
    dispatch(postProcessInvoice({ sessionId: selectedSessionId }))
      .unwrap()
      .then(() =>
        trackRevenue({
          orderList: selectedSessionProducts,
          currencyCode: selectedSessionCurrencyCode,
          purchaseFlow: getPurchaseFlow(selectedClientIsEcom, selectedSessionCancelUrl),
          paymentMethod: getPaymentTrackingType(paymentType, selectedPaymentMethodTypes),
          quoteId: selectedInvoiceNumber,
          clientURL: selectedSessionClientUrl,
          total: selectedQuoteTotalPrice,
        }),
      );
  }, [
    dispatch,
    selectedInvoiceNumber,
    paymentType,
    selectedClientIsEcom,
    selectedPaymentMethodTypes,
    selectedQuoteTotalPrice,
    selectedSessionCancelUrl,
    selectedSessionClientUrl,
    selectedSessionCurrencyCode,
    selectedSessionId,
    selectedSessionProducts,
  ]);

  useEffect(() => {
    if (
      selectedTransactionUseAchFailed ||
      selectedTransactionUseDDFailed ||
      selectedTransactionUsePaypalFailed ||
      selectedRazorpayIsFailed
    ) {
      Track(PayNowEventNames.paynowCTA, {
        status: TRACKING_EVENT_STATUS.copasFailure,
        paymentType: getPaymentTrackingType(paymentType, selectedPaymentMethodTypes),
        paymentMethodKey: paymentMethodKey.slice(-5),
      });
      dispatch(showErrorSnack(st['alert.error.paymentmethod.general.failure']));
    }
  }, [
    dispatch,
    paymentMethodKey,
    paymentType,
    selectedPaymentMethodTypes,
    selectedTransactionUseAchFailed,
    selectedTransactionUseDDFailed,
    selectedTransactionUsePaypalFailed,
    selectedRazorpayIsFailed,
  ]);

  useEffect(() => {
    if (
      selectedTransactionUseAchSuccess ||
      selectedTransactionUseDDSuccess ||
      selectedTransactionUsePaypalSuccess ||
      selectedTransactionUseDDPending ||
      selectedTransactionUseAchPending ||
      selectedTransactionUsePaypalPending ||
      selectedRazorpayIsSuccess ||
      selectedRazorpayIsPending
    ) {
      if (selectedIsNewTabPayment) window.close();
      else if (renewalDate) processRenwal();
      else if (selectedInvoiceNumber) processInvoice();
      else processQuote();
    }
  }, [
    selectedTransactionUseAchSuccess,
    selectedTransactionUseDDSuccess,
    processRenwal,
    processQuote,
    renewalDate,
    selectedTransactionUseDDPending,
    selectedTransactionUseAchPending,
    selectedTransactionUsePaypalSuccess,
    selectedTransactionUsePaypalPending,
    selectedRazorpayIsSuccess,
    selectedRazorpayIsPending,
    selectedIsNewTabPayment,
    selectedInvoiceNumber,
    processInvoice,
  ]);

  // Pay now button click handler
  const payNow = () => {
    isDisabled = true;
    GATrack(GAEventNames.ClickCTA, { ctaName: st['pay.now.cta'].defaultMessage });
    Track(PayNowEventNames.paynowCTA, {
      status: TRACKING_EVENT_STATUS.click,
      paymentType: getPaymentTrackingType(paymentType, selectedPaymentMethodTypes),
      paymentMethodKey: paymentMethodKey.slice(-5),
    });
    setComponentLoading(true);
    const payload = {
      fingerPrintSessionId: selectedFingerprintId,
      paymentMethodKey: paymentMethodKey,
    };

    switch (paymentType) {
      case PAYMENT_METHODS_TYPES.ach:
        if (isAdyen) {
          dispatch(postTransactionUseAdyenAchPay(payload))
            .unwrap()
            .catch(() => {
              GATrack(GAEventNames.PurchaseError, {
                products: selectedSessionProducts,
                paymentMethod: PAYMENT_METHODS_TYPES.ach,
                errMsg: GAEventNames.PAYMENT_FAILED,
              });
              if (selectedIsNewTabPayment) {
                window.close();
              }
              dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
              setComponentLoading(false);
            });
        } else {
          dispatch(postTransactionUseAchPay(payload))
            .unwrap()
            .catch(() => {
              GATrack(GAEventNames.PurchaseError, {
                products: selectedSessionProducts,
                paymentMethod: PAYMENT_METHODS_TYPES.ach,
                errMsg: GAEventNames.PAYMENT_FAILED,
              });
              if (selectedIsNewTabPayment) {
                window.close();
              }
              dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
              setComponentLoading(false);
            });
        }
        break;

      case PAYMENT_METHODS_TYPES.dd:
        dispatch(postTransactionUseDDPay(payload))
          .unwrap()
          .catch(() => {
            GATrack(GAEventNames.PurchaseError, {
              products: selectedSessionProducts,
              paymentMethod: PAYMENT_METHODS_TYPES.dd,
              errMsg: GAEventNames.PAYMENT_FAILED,
            });
            if (selectedIsNewTabPayment) window.close();
            dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
            setComponentLoading(false);
          });
        break;

      case PAYMENT_METHODS_TYPES.paypal:
        dispatch(postTransactionUsePaypalPay(payload))
          .unwrap()
          .catch(() => {
            GATrack(GAEventNames.PurchaseError, {
              products: selectedSessionProducts,
              paymentMethod: PAYMENT_METHODS_TYPES.paypal,
              errMsg: GAEventNames.PAYMENT_FAILED,
            });
            if (selectedIsNewTabPayment) window.close();
            dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
            setComponentLoading(false);
          });
        break;

      case PAYMENT_METHODS_TYPES.razorpay:
        dispatch(postTransactionUseRazorPay(payload))
          .unwrap()
          .catch(() => {
            GATrack(GAEventNames.PurchaseError, {
              products: selectedSessionProducts,
              paymentMethod: PAYMENT_METHODS_TYPES.razorpay,
              errMsg: GAEventNames.PAYMENT_FAILED,
            });
            if (selectedIsNewTabPayment) window.close();
            dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
            setComponentLoading(false);
          });
    }
  };

  const isButtonLoading =
    isComponentLoading ||
    selectedQuoteProcessIsLoading ||
    selectedProcessRenewalIsLoading ||
    selectedTransactionAchLoading ||
    selectedTransactionDDLoading ||
    selectedTransactionPaypalLoading ||
    selectedUseRazorPayLoading ||
    selectedProcessInvoiceIsLoading;

  const isButtonDisabled =
    isDisabled || selectedDeletePaymentMethodIsLoading || selectedSetDefaultPaymentIsLoading || !paymentMethodKey;

  return (
    <Button isLoading={isButtonLoading} disabled={isButtonDisabled} size="large" fullWidth onClick={payNow}>
      {intl.formatMessage(st['pay.now.cta'])}
    </Button>
  );
};

export default ElectronicBuyNow;
